import { getCapacitorSessionBearerToken } from '~~/layers/base/lib/auth-client'
import {
    appendDesignerCompatibilityRequestHeaders,
    wrapFetchOptionsWithMinAppVersionOnResponse,
} from '~~/layers/base/utils/designerMinAppVersionPolicy'

type DesignerFetchOptions = Record<string, unknown> & {
    baseURL?: string
    headers?: HeadersInit
}

/**
 * Merges options for client `$fetch` calls that use a configured API `baseURL` (cross-origin from the Capacitor shell).
 * Capacitor (`capacitor://localhost`) does not run those requests through Better Auth's `authClient`
 * hooks, so the session bearer must be attached here — same token as `useApiFetch`.
 * In the Capacitor shell, also attaches min-version request headers and `onResponse` enforcement
 * per `doc/spec/SPEC_force_update_semver.md`.
 */
export async function mergeCapacitorDesignerFetchOptions<T extends DesignerFetchOptions>(
    options: T
): Promise<T> {
    const headers = new Headers(options.headers as HeadersInit | undefined)
    if (import.meta.client) {
        const bearer = await getCapacitorSessionBearerToken()
        if (bearer) {
            headers.set('Authorization', `Bearer ${bearer}`)
        }
        await appendDesignerCompatibilityRequestHeaders(
            headers,
            options.baseURL as string | undefined
        )
    }
    const baseURL = options.baseURL
    const merged = {
        ...options,
        headers,
        ...(baseURL ? { credentials: 'include' as const } : {}),
    } as T

    return wrapFetchOptionsWithMinAppVersionOnResponse(merged)
}
